// src/pages/notifications/TeamSettingsPage.tsx
// Team notification settings: enable/disable, event rules, role rules.

import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsSetting, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, Badge, ErrorAlert } from '@react/components';
import { FormRow } from '@/components/form';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { EventRulesEditor } from '../../features/notifications/EventRulesEditor';
import type { TeamNotificationSettings } from '@core/types/notifications/TeamNotificationSettings';
import type { TeamEventRule } from '@core/types/notifications/TeamEventRule';
import type { RoleNotificationRule } from '@core/types/notifications/RoleNotificationRule';
import type { EventNotificationRule } from '@core/types/notifications/EventNotificationRule';

export const TeamSettingsPage = observer(() => {
  const store = useNotificationsSetting();
  const { t } = useI18n();
  const { teamId } = useParams<{ teamId: string }>();
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [eventRules, setEventRules] = useState<TeamEventRule[]>([]);
  const [roleRules, setRoleRules] = useState<RoleNotificationRule[]>([]);

  // Member overrides (notifications.GetTeamMemberOverrides / UpdateTeamMemberOverrides)
  // — a specific member's personal deviation from the team defaults above.
  const [memberUserId, setMemberUserId] = useState('');
  const [memberNotificationsEnabled, setMemberNotificationsEnabled] = useState(true);
  const [memberEventRules, setMemberEventRules] = useState<EventNotificationRule[]>([]);
  const [isLoadingMemberOverrides, setIsLoadingMemberOverrides] = useState(false);
  const [isSavingMemberOverrides, setIsSavingMemberOverrides] = useState(false);
  const [memberOverridesLoaded, setMemberOverridesLoaded] = useState(false);
  const [memberOverridesSaved, setMemberOverridesSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!teamId) return;
      try {
        const resp = await store.GetTeamSettings(teamId);
        const s = resp as TeamNotificationSettings | null;
        if (s && !loaded) {
          setNotificationsEnabled(s.notifications_enabled ?? true);
          setEventRules((s.team_event_rules as unknown as { items?: TeamEventRule[] })?.items ?? []);
          setRoleRules((s.role_rules as unknown as { items?: RoleNotificationRule[] })?.items ?? []);
          setLoaded(true);
        }
      } catch {
        setLoaded(true);
      }
    };
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!teamId) return;
    try {
      await store.UpdateTeamSettings(teamId, {
        notifications_enabled: notificationsEnabled,
        team_event_rules: { items: eventRules },
        role_rules: { items: roleRules },
      } as never);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // handled by store
    }
  };

  const handleLoadMemberOverrides = async () => {
    if (!teamId || !memberUserId.trim()) return;
    setIsLoadingMemberOverrides(true);
    setMemberOverridesLoaded(false);
    try {
      const resp = await store.GetTeamMemberOverrides(teamId, memberUserId.trim());
      setMemberNotificationsEnabled(resp.overrides?.notifications_enabled ?? true);
      setMemberEventRules(resp.overrides?.personal_overrides?.items ?? []);
      setMemberOverridesLoaded(true);
    } catch {
      // No overrides row yet for this member — start from a blank slate
      // rather than surfacing an error banner for the normal first case.
      store.clearError();
      setMemberNotificationsEnabled(true);
      setMemberEventRules([]);
      setMemberOverridesLoaded(true);
    } finally {
      setIsLoadingMemberOverrides(false);
    }
  };

  const handleSaveMemberOverrides = async () => {
    if (!teamId || !memberUserId.trim()) return;
    setIsSavingMemberOverrides(true);
    try {
      await store.UpdateTeamMemberOverrides(teamId, memberUserId.trim(), {
        overrides: {
          notifications_enabled: memberNotificationsEnabled,
          personal_overrides: { items: memberEventRules },
        },
      } as never);
      setMemberOverridesSaved(true);
      setTimeout(() => setMemberOverridesSaved(false), 3000);
    } catch {
      // handled by store
    } finally {
      setIsSavingMemberOverrides(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/notifications" className="text-sm text-primary hover:text-primary/80">{t('notifications.list')}</Link>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('notifications.teamSettings')}</h1>
      </div>

      {saved && <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>{t('notifications.saveSuccess')}</Alert>}
      {store.error ? <ErrorAlert error={store.error} /> : null}

      <Card>
        <CardHeader><CardTitle>{t('notifications.generalSettings')}</CardTitle></CardHeader>
        <CardContent>
          <FormRow label={t('notifications.notificationsEnabled')}>
            <ToggleSwitch label="" description={t('notifications.teamNotificationsEnabledDesc')} checked={notificationsEnabled} onChange={setNotificationsEnabled} />
          </FormRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.teamEventRules')} ({eventRules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {eventRules.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('notifications.noEventRules')}</p>
          ) : (
            <div className="space-y-3">
              {eventRules.map((rule, idx) => (
                <div key={idx} className="p-3 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{rule.event_type}</span>
                    <Badge variant={rule.enabled ? 'success' : 'secondary'}>{rule.enabled ? t('notifications.enabled') : t('notifications.disabled')}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    {rule.target_roles?.length > 0 && <p>{t('notifications.targetRoles')}: {rule.target_roles.join(', ')}</p>}
                    {rule.notify_all_members && <p>{t('notifications.notifyAllMembers')}</p>}
                    {rule.is_mandatory && <Badge variant="warning" className="text-xs">{t('notifications.mandatory')}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.roleRules')} ({roleRules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {roleRules.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('notifications.noRoleRules')}</p>
          ) : (
            <div className="space-y-3">
              {roleRules.map((rule, idx) => (
                <div key={idx} className="p-3 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{rule.role_name || rule.role_id}</span>
                    {rule.is_mandatory && <Badge variant="warning">{t('notifications.mandatory')}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {rule.notification_types?.length > 0 && <p>{t('notifications.notificationTypes')}: {rule.notification_types.join(', ')}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button onClick={handleSave} isLoading={store.isLoading}>{t('common.save')}</Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.memberOverrides.title')}</CardTitle>
          <CardDescription>{t('notifications.memberOverrides.desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <Input
              label={t('notifications.memberOverrides.userId')}
              value={memberUserId}
              onChange={(e) => { setMemberUserId(e.target.value); setMemberOverridesLoaded(false); }}
              className="flex-1"
            />
            <Button variant="secondary" onClick={handleLoadMemberOverrides} isLoading={isLoadingMemberOverrides} disabled={!memberUserId.trim()}>
              {t('common.load')}
            </Button>
          </div>

          {memberOverridesSaved && <Alert variant="success">{t('notifications.saveSuccess')}</Alert>}

          {memberOverridesLoaded && (
            <>
              <FormRow label={t('notifications.notificationsEnabled')}>
                <ToggleSwitch
                  label=""
                  checked={memberNotificationsEnabled}
                  onChange={setMemberNotificationsEnabled}
                />
              </FormRow>
              <EventRulesEditor rules={memberEventRules} onChange={setMemberEventRules} />
            </>
          )}
        </CardContent>
        {memberOverridesLoaded && (
          <CardFooter>
            <Button onClick={handleSaveMemberOverrides} isLoading={isSavingMemberOverrides}>{t('common.save')}</Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
});

export default TeamSettingsPage;
